import { useState, useEffect, useMemo, useRef } from 'react';

import gql from 'graphql-tag';
import { useDebouncedCallback } from 'use-debounce';

import { useLazyQuery } from '@apollo/client';

import { LaneType } from 'common-types';
import { getClient } from 'lane-shared/apollo';
import { BlockSortDirection } from 'constants-content';
import { safeConvertToUUID } from 'uuid-encoding';
import {
  NumericSearchTypeEnum,
  StringSearchTypeEnum,
} from 'constants-activate';
import { UserContentInteraction } from 'graphql-query-contracts';

/**
 * V3 of the interactions query - optimized for UCI table with company column.
 * Removed unnecessary fields: _createdBy, _updatedBy, geo, state, content, version
 * to reduce payload size.
 */
const queryV3 = gql`
  query interactionsOnChannelQueryV3(
    $channelId: UUID!
    $sectionId: UUID
    $paymentAccountId: UUID
    $merchantAccountId: UUID
    $search: UserContentInteractionSearch
    $pagination: PaginationInput
  ) {
    interactionsOnChannel(
      channelId: $channelId
      sectionId: $sectionId
      paymentAccountId: $paymentAccountId
      merchantAccountId: $merchantAccountId
      search: $search
      pagination: $pagination
    ) {
      pageInfo {
        total
        start
        perPage
      }
      items {
        _id
        _created
        _updated
        user {
          _id
          name
          profile {
            name
          }
        }
        startDate
        endDate
        data
        features
        contentData
        status
        companiesConnection(first: 100) {
          totalCount
          edges {
            node {
              _id
              name
            }
          }
        }
      }
    }
  }
`;

/**
 * Type representing the subset of UserContentInteraction fields
 * returned by the V3 query (optimized for UCI table with company column).
 * Derived from UserContentInteraction to ensure type compatibility.
 */
export type InteractionWithCompanies = Pick<
  UserContentInteraction,
  | '_id'
  | '_created'
  | '_updated'
  | 'user'
  | 'startDate'
  | 'endDate'
  | 'data'
  | 'features'
  | 'contentData'
  | 'status'
  | 'companiesConnection'
>;

export interface variablesTypeV3 {
  channelId: LaneType.UUID;
  sectionId: LaneType.UUID;
  merchantAccountId: LaneType.UUID;
  paymentAccountId: LaneType.UUID;
  pagination: {
    start: number;
    perPage: number;
  };
  search: {
    searchValue?: object;
    sortBy: {
      key: string;
      dir: BlockSortDirection;
    };
    state?: {
      isOpen: boolean;
      isActive?: boolean;
    };
    user?: {
      name?: {
        type: StringSearchTypeEnum;
        value: string;
      };
    };
    _created?: {
      type: NumericSearchTypeEnum;
      value: Date;
      valueB: Date;
    };
    startDate?: {
      type: NumericSearchTypeEnum;
      value: Date;
      valueB: Date;
    };
    status?: {
      type: 'eq';
      value: string;
    };
    company?: {
      type: 'eq';
      value: string;
    };
  };
}

const defaultSearch = {};

type Props = {
  channelId: LaneType.UUID;
  paymentAccountId?: LaneType.UUID;
  sectionId?: LaneType.UUID;
  merchantAccountId?: LaneType.UUID;
  selectedSort?: string;
  selectedOrder?: string;
  selectedState?: string;
  selectedStatus?: string;
  perPage?: number;
  search?: Record<string, unknown>;
  keyword?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  startCreationDate?: Date | null;
  endCreationDate?: Date | null;
  companyId?: string;
};

export function useInteractionsOnChannelQueryV3({
  channelId,
  paymentAccountId,
  sectionId,
  merchantAccountId,
  selectedSort = '_updated',
  selectedOrder = 'asc',
  selectedState = 'any',
  selectedStatus = 'any',
  perPage = 100,
  search = defaultSearch,
  keyword,
  startDate,
  endDate,
  startCreationDate,
  endCreationDate,
  companyId,
}: Props) {
  const [page, setPage] = useState(0);
  const [searchValue, setSearchValue] = useState(search);

  const debouncedSearchValue = useDebouncedCallback(function () {
    setSearchValue(search);
  }, 500).callback;

  const [fetchInteractions, { data, loading, error }] = useLazyQuery(queryV3, {
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    debouncedSearchValue();
  }, [JSON.stringify(search)]);

  const interactions = useMemo(() => {
    if (data?.interactionsOnChannel?.items) {
      return data.interactionsOnChannel.items as InteractionWithCompanies[];
    }

    return [] as InteractionWithCompanies[];
  }, [data?.interactionsOnChannel?.items]);

  const pageInfo = useMemo(() => {
    if (data?.interactionsOnChannel?.pageInfo) {
      return data.interactionsOnChannel.pageInfo;
    }

    return {
      total: 0,
      start: 0,
      perPage,
    };
  }, [perPage, data?.interactionsOnChannel?.pageInfo]);

  const variables = useMemo(() => {
    const searchObj: Record<string, unknown> = {
      ...searchValue,
      sortBy: {
        key: selectedSort,
        dir: selectedOrder,
      },
    };

    if (keyword) {
      searchObj.search = {
        type: StringSearchTypeEnum.Like,
        value: keyword,
      };
    }

    if (selectedState === 'open') {
      searchObj.state = {
        isOpen: true,
      };
    } else if (selectedState === 'closed') {
      searchObj.state = {
        isOpen: false,
      };
    }

    if (selectedStatus && selectedStatus !== 'any') {
      searchObj.status = {
        type: 'eq',
        value: selectedStatus,
      };
    }

    if (companyId) {
      searchObj.company = {
        type: 'eq',
        value: safeConvertToUUID(companyId),
      };
    }

    const created =
      startCreationDate && endCreationDate
        ? {
            type: NumericSearchTypeEnum.Between,
            value: startCreationDate,
            valueB: endCreationDate,
          }
        : null;

    const reservation =
      startDate && endDate
        ? {
            type: NumericSearchTypeEnum.Between,
            value: startDate,
            valueB: endDate,
          }
        : null;

    if (created) {
      searchObj._created = created;
    }

    if (reservation) {
      searchObj.startDate = reservation;
    }

    return {
      channelId,
      sectionId,
      merchantAccountId,
      paymentAccountId,
      pagination: {
        start: page * perPage,
        perPage,
      },
      search: searchObj,
    };
  }, [
    channelId,
    sectionId,
    paymentAccountId,
    merchantAccountId,
    page,
    perPage,
    selectedOrder,
    selectedSort,
    selectedState,
    selectedStatus,
    searchValue,
    keyword,
    startDate,
    endDate,
    startCreationDate,
    endCreationDate,
    companyId,
  ]);

  const lastFetchedVariables = useRef<string>();

  function refresh() {
    const variablesJson = JSON.stringify(variables);

    lastFetchedVariables.current = variablesJson;
    fetchInteractions({
      variables,
    });
  }

  useEffect(() => {
    const variablesJson = JSON.stringify(variables);

    if (
      (channelId || sectionId || paymentAccountId || merchantAccountId) &&
      !loading &&
      variablesJson !== lastFetchedVariables.current
    ) {
      lastFetchedVariables.current = variablesJson;
      fetchInteractions({
        variables,
      });
    }
  }, [variables, loading]);

  return {
    interactions,
    pageInfo,
    page,
    perPage,
    loading,
    error,
    setPage,
    refresh,
    variables,
  };
}

